import { useEffect, useMemo, useState } from "react";
import type { StatusColor } from "../../../../components/Status/Status";
import { StatusLeft } from "../../../../components/Status/Status";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  formatDateTime,
  toTitleCase,
  useStoreState,
} from "../../../../util/util";
import type { ColumnDef, Getter } from "@tanstack/react-table";
import { IDMobile } from "../../../../components/IDs/IDs";
import useSWR from "swr";
import type { WithPagination } from "../../../../types/types";
import { InvisibleButton } from "../../../../components/Buttons/Buttons";
import { DownloadIcon } from "../../../../components/Icons/Icons";
import type { SortingRule } from "react-table";
import { H3 } from "../../../../components/Typography/Typography";
import { DropdownContainer } from "../../../../components/Layout/Layout";
import { DropDown } from "../../../../components/DropDown/DropDown";
import { ContentWrapper } from "../../../../layout/portalPageLayout";
import { Table } from "../../../../components/Table/Table";
import { Pagination } from "../../../../components/Pagination/Pagination";
import { BooleanParam, useQueryParams } from "use-query-params";
import type { CustomerUploadSchema } from "./types";
import axios from "axios";

const getStatusColor = (
  status: CustomerUploadSchema["status"]
): StatusColor => {
  switch (status) {
    case "completed_with_errors":
    case "failed":
      return "red";
    case "completed":
      return "green";
    case "pending":
    case "processing":
      return "yellow";
    default:
      return "orange";
  }
};

const getStatusText = (
  status: CustomerUploadSchema["status"],
  t: TFunction
): string => {
  switch (status) {
    case "completed_with_errors":
    case "failed":
      return t("Failed");
    case "completed":
      return t("Successful");
    case "pending":
    case "processing":
      return t("In Progress");
    default:
      return t("Paused");
  }
};

export const PreviousCustomersUploadTable = ({
  customer_type,
}: {
  customer_type: "customers" | "distributors";
}) => {
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "desc",
  });
  const [tableData, setTableData] = useState<
    (CustomerUploadSchema & { id: string })[]
  >([]);
  const [offset, setOffset] = useState(0);
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [query, setQuery] = useQueryParams({
    shouldMutate: BooleanParam,
  });

  const { storefront_id, tenant_id } = useStoreState();
  const { t } = useTranslation();

  const tableColumns = useMemo<ColumnDef<CustomerUploadSchema>[]>(
    () => [
      {
        header: t("Upload ID"),
        accessorKey: "number",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <IDMobile>{getValue()}</IDMobile>
        ),
      },
      {
        header: t("Uploaded on"),
        accessorKey: "updated_on",
        enableSorting: false,
        cell: (cell) => cell.renderValue(),
      },
      {
        header: t("Uploaded By"),
        accessorKey: "uploaded_by",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <span>{toTitleCase(getValue())}</span>
        ),
      },
      {
        header: t("Status"),
        accessorKey: "status",
        enableSorting: false,
        cell: ({
          getValue,
        }: {
          getValue: Getter<CustomerUploadSchema["status"]>;
        }) => (
          <StatusLeft
            color={getStatusColor(getValue())}
            text={getStatusText(getValue(), t)}
            textStyle={{ fontSize: "13px" }}
          />
        ),
      },
      {
        header: "",
        accessorKey: "download_url",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string | undefined> }) =>
          getValue() ? (
            <a
              href={getValue()!}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", width: "fit-content" }}
            >
              <InvisibleButton title={t("Log report")}>
                <DownloadIcon width={16} height={16} />
              </InvisibleButton>
            </a>
          ) : (
            <></>
          ),
      },
    ],
    [t]
  );

  const customer_type_param = useMemo(() => {
    return customer_type === "customers" ? "Buyer" : "Distributor";
  }, [customer_type]);

  const {
    data: previousUploadsResponse,
    error,
    mutate,
  } = useSWR<WithPagination<{ data: CustomerUploadSchema[] }>>(
    `/v2/storefronts/${storefront_id}/tenants/bulk_upload_jobs?type=${customer_type_param}&offset=${offset}&limit=${perPage}&order_by=${
      sortingRules.orderBy ?? "desc"
    }`
  );

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const changePerPage = (perPage: number) => {
    setPerPage(perPage);
    if (perPage > offset) {
      setOffset(0);
    }
  };

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const isLoading = !previousUploadsResponse && !error;

  const hasData =
    previousUploadsResponse && previousUploadsResponse.data.length > 0;

  useEffect(() => {
    const handlePreviousUpdatesData = ({
      data,
      pagination,
    }: WithPagination<{ data: CustomerUploadSchema[] }>) => {
      setTableData(
        data.map(({ upload_id, updated_on, ...rest }) => ({
          ...rest,
          upload_id,
          id: upload_id,
          updated_on: formatDateTime(updated_on),
          download_url: `${axios.defaults.baseURL}/v2/storefronts/${storefront_id}/tenants/${tenant_id}/bulk_download/${upload_id}`,
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };
    if (previousUploadsResponse) {
      handlePreviousUpdatesData(previousUploadsResponse);
    }
  }, [perPage, previousUploadsResponse, storefront_id, tenant_id]);

  useEffect(() => {
    if (query.shouldMutate) {
      mutate();
      setQuery({ shouldMutate: false });
    }
  }, [query.shouldMutate, mutate, setQuery]);

  return hasData ? (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <H3 style={{ margin: 0 }}>{t("Previous Uploads")}</H3>
      <div>
        <DropdownContainer>
          <DropDown
            items={perPageItems}
            activeItem={perPage}
            textLeft={t("items") + ":"}
            textRight={t(" Per Page")}
            direction={"right"}
            className={"per_Page"}
            clickHandler={changePerPage}
          />
        </DropdownContainer>
        <ContentWrapper>
          <Table
            columns={tableColumns}
            data={tableData}
            isLoading={isLoading}
            error={error}
            handleSort={handleSort}
            lastChildleftAlign={true}
          />
          <Pagination
            pagination={tablePagination}
            offset={offset}
            handlePageClick={changePage}
          />
        </ContentWrapper>
      </div>
    </div>
  ) : (
    <></>
  );
};
